/** Runtime capability detection; never assume a browser brand. RUN-005, AC-012 */
export interface BrowserCapabilities {
  opfs: boolean;
  workers: boolean;
  mse: boolean;
  /** iOS Safari exposes ManagedMediaSource instead of MediaSource. */
  managedMse: boolean;
  codecs: { h264: boolean; aac: boolean; vp9: boolean; av1: boolean; opus: boolean; mp3: boolean };
  savePicker: boolean;
  serviceWorker: boolean;
  webCodecs: boolean;
  storagePersist: boolean;
  /** Narrow, touch-first device. Reduces concurrency and memory ceilings. */
  constrained: boolean;
  standalone: boolean;
  ios: boolean;
}

type MSConstructor = {
  new (): MediaSource;
  isTypeSupported(type: string): boolean;
};

export function mediaSourceCtor(): MSConstructor | null {
  const w = globalThis as unknown as {
    ManagedMediaSource?: MSConstructor;
    MediaSource?: MSConstructor;
  };
  return w.ManagedMediaSource ?? w.MediaSource ?? null;
}

function supports(type: string): boolean {
  try {
    return mediaSourceCtor()?.isTypeSupported(type) ?? false;
  } catch {
    return false;
  }
}

export function detectCapabilities(): BrowserCapabilities {
  const w = globalThis as unknown as Record<string, unknown>;
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const ua = nav?.userAgent ?? '';
  const ios =
    /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (nav?.maxTouchPoints ?? 0) > 1);
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  const standalone =
    (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) ||
    (nav as unknown as { standalone?: boolean } | undefined)?.standalone === true;
  const mse = mediaSourceCtor() !== null;
  return {
    opfs: !!nav?.storage && typeof nav.storage.getDirectory === 'function',
    workers: typeof Worker !== 'undefined',
    mse,
    managedMse: 'ManagedMediaSource' in w,
    codecs: {
      h264: supports('video/mp4; codecs="avc1.64001f"'),
      aac: supports('audio/mp4; codecs="mp4a.40.2"'),
      vp9: supports('video/webm; codecs="vp9"') || supports('video/mp4; codecs="vp09.00.10.08"'),
      av1: supports('video/mp4; codecs="av01.0.05M.08"'),
      opus: supports('audio/webm; codecs="opus"') || supports('audio/mp4; codecs="opus"'),
      mp3: supports('audio/mpeg'),
    },
    savePicker: typeof w['showSaveFilePicker'] === 'function',
    serviceWorker: !!nav && 'serviceWorker' in nav,
    webCodecs: typeof w['VideoDecoder'] === 'function',
    storagePersist: !!nav?.storage && typeof nav.storage.persist === 'function',
    constrained: coarse || ios || (typeof innerWidth === 'number' && innerWidth < 640),
    standalone,
    ios,
  };
}

/** Default concurrent downloads: two on desktop, one on mobile. ALG-061 */
export function defaultConcurrency(c: BrowserCapabilities): number {
  return c.constrained ? 1 : 2;
}

/** Largest image/Blob we are willing to build in memory. ARCH-009, ALG-044 */
export function blobBudgetBytes(c: BrowserCapabilities): number {
  return c.constrained ? 48 * 1024 * 1024 : 256 * 1024 * 1024;
}
